import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, fmtRMSigned } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { PeriodSelector, periodLabel, periodParams, periodQuery } from "@/components/PeriodSelector";
import {
  Wallet,
  TrendingUp,
  Landmark,
  Banknote,
  Coins,
  PiggyBank,
  ArrowUpRight,
  PlusCircle,
  AlertTriangle,
  Receipt,
  MoonStar,
} from "lucide-react";

function Card({ label, value, icon: Icon, accent, testid, sub, to, valueClass }) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{label}</span>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className={`mt-3 font-mono-fig text-2xl font-bold tracking-tight ${valueClass || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className="block bg-white border border-slate-200 rounded-lg p-5 shadow-sm transition-all hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5 cursor-pointer"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      {inner}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const canEdit = user?.canEdit;
  const [summary, setSummary] = useState(null);
  const [loans, setLoans] = useState([]);
  const [collection, setCollection] = useState([]);
  const [period, setPeriod] = useState("month");
  const [periodVal, setPeriodVal] = useState(new Date().toISOString().slice(0, 7));
  const [verifyCount, setVerifyCount] = useState(0);

  const loadOverview = useCallback(() => {
    api.get("/loans").then((l) => {
      const sorted = [...l.data].sort((a, b) => (b.dateApplied || "").localeCompare(a.dateApplied || ""));
      setLoans(sorted.slice(0, 6));
    });
    api.get("/reports/collection-priority").then((c) => setCollection(c.data.slice(0, 5)));
    api.get("/reports/data-health").then((d) => setVerifyCount((d.data.futureDates || []).length)).catch(() => {});
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const loadSummary = useCallback(() => {
    api.get("/dashboard/summary", { params: periodParams(period, periodVal) }).then((s) => setSummary(s.data));
  }, [period, periodVal]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const onPeriodChange = (p, v) => { setPeriod(p); setPeriodVal(v); };
  const pLabel = periodLabel(period, periodVal);
  const pQuery = periodQuery(period, periodVal);
  const shortPeriod = period === "last7" || period === "last30";
  const showOutstanding = period === "all";

  return (
    <Layout>
      <PageHeader
        title={t("dash.title")}
        subtitle={t("dash.subtitle")}
        action={
          canEdit ? (
            <Link
              to="/loans/new"
              data-testid="dashboard-new-loan-btn"
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <PlusCircle className="h-4 w-4" /> {t("common.new_loan")}
            </Link>
          ) : null
        }
      />

      {verifyCount > 0 && (
        <Link
          to="/data-health"
          data-testid="verify-data-banner"
          className="flex items-start gap-3 mb-5 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 hover:bg-amber-100 transition-colors"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900">
              {verifyCount} {t("dash.verify_records")}
            </div>
            <div className="text-amber-700">
              {t("dash.verify_desc")}
            </div>
          </div>
        </Link>
      )}

      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="dashboard-period-selector">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-1">{t("common.period")}</span>
        <PeriodSelector period={period} value={periodVal} onChange={onPeriodChange} hideQuick />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6">
        <Card
          testid="total-capital-card"
          label={t("dash.loan_disbursement")}
          value={fmtRM(summary?.totalCapitalInvested)}
          icon={Landmark}
          accent="bg-slate-100 text-slate-700"
          sub={`${summary?.totalLoans || 0} ${t("dash.loans_word")} · ${pLabel}`}
          to={`/disbursement${pQuery}`}
        />
        <Card
          testid="cash-balance-card"
          label={t("dash.cash_balance")}
          value={fmtRMSigned(summary?.currentCashBalance)}
          icon={Wallet}
          accent="bg-blue-100 text-blue-700"
          sub={t("dash.live_ledger")}
        />
        {!shortPeriod && (
          <Card
            testid="income-card"
            label={t("dash.income")}
            value={fmtRM(summary?.incomeCollected)}
            icon={Coins}
            accent="bg-green-100 text-green-700"
            sub={`${t("dash.interest_collected")} · ${pLabel}`}
          />
        )}
        {!shortPeriod && (
          <Card
            testid="operating-expenses-card"
            label={t("dash.operating_expenses")}
            value={fmtRM(summary?.totalExpenses)}
            icon={Receipt}
            accent="bg-rose-100 text-rose-700"
            sub={`${t("dash.office_salary")} · ${pLabel}`}
          />
        )}
        {!shortPeriod && (
          <Card
            testid="net-profit-card"
            label={t("dash.net_profit")}
            value={fmtRMSigned(summary?.netProfit)}
            icon={TrendingUp}
            accent={summary?.netProfit >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
            valueClass={summary?.netProfit >= 0 ? "text-green-600" : "text-red-600"}
            sub={period === "all" ? t("dash.np_all") : `${t("dash.interest_collected")} ${fmtRM(summary?.incomeCollected)}`}
            to={`/net-profit${pQuery}`}
          />
        )}
        {showOutstanding && (
          <Card
            testid="capital-invested-card"
            label={t("dash.capital_invested")}
            value={fmtRM(summary?.capitalInvested)}
            icon={PiggyBank}
            accent="bg-indigo-100 text-indigo-700"
            sub={t("dash.capital_sub")}
          />
        )}
        {showOutstanding && (
          <Card
            testid="outstanding-card"
            label={t("dash.outstanding")}
            value={fmtRM(summary?.totalOutstanding)}
            icon={Banknote}
            accent="bg-amber-100 text-amber-700"
            sub={`${summary?.activeOutstandingCount || 0} ${t("dash.outstanding_sub")}`}
            to={`/outstanding${pQuery}${pQuery.includes("?") ? "&" : "?"}filter=active`}
          />
        )}
      </div>

      <Link
        to={`/outstanding${pQuery}${pQuery.includes("?") ? "&" : "?"}filter=inactive`}
        data-testid="inactive-outstanding-strip"
        className="mt-4 flex items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-lg px-5 py-3 hover:bg-red-100 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-red-100 flex items-center justify-center">
            <MoonStar className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-red-700">{t("dash.inactive_title")}</div>
            <div className="text-xs text-red-500">{summary?.inactiveOutstandingCount || 0} {t("dash.inactive_desc")}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono-fig text-xl font-bold text-red-700">{fmtRM(summary?.inactiveOutstanding)}</span>
          <ArrowUpRight className="h-4 w-4 text-red-400 group-hover:text-red-600 transition-colors" />
        </div>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Recent loans */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">{t("dash.recent_loans")}</h3>
            <Link to="/loans" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
              {t("common.view_all")} <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5 font-medium">{t("col.code")}</th>
                <th className="px-3 py-2.5 font-medium">{t("col.customer")}</th>
                <th className="px-3 py-2.5 font-medium">{t("col.applied")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("col.outstanding")}</th>
                <th className="px-5 py-2.5 font-medium">{t("col.status")}</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => (
                <tr key={l.code} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3">
                    <Link to={`/loans/${l.code}`} className="font-mono-fig font-medium text-slate-900 hover:underline">
                      {l.code}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{l.customerName}</td>
                  <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{l.dateApplied || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(l.outstanding)}</td>
                  <td className="px-5 py-3"><StatusBadge status={l.status} /></td>
                </tr>
              ))}
              {loans.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400 text-sm">{t("dash.no_loans")}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Collection priority */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> {t("dash.collection_risk")}
            </h3>
            <Link to="/collection" className="text-xs text-slate-500 hover:text-slate-900">{t("common.view")}</Link>
          </div>
          <div className="divide-y divide-slate-50">
            {collection.map((c) => (
              <div key={c.code} className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono-fig text-sm font-medium text-slate-900">{c.code}</span>
                  <span className="font-mono-fig text-sm text-red-600">{fmtRM(c.outstanding)}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{c.customerName}</div>
                <div className="text-[11px] text-amber-700 mt-1">{c.reasons[0]}</div>
              </div>
            ))}
            {collection.length === 0 && (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("dash.no_risk")}</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
